import {
  type BoxProps,
  type CustomerQuote,
  Divider,
  Flex,
  HStack,
  Icon,
  IconName,
  InlineFormattedNumber,
  negotiationStatusMakerDescription,
  negotiationStatusMakerLabel,
  Text,
  Tooltip,
  Unit,
} from '@talos/kyoko';
import type { ReactNode } from 'react';

export const NegotiationStatusLabel = (props: BoxProps) => {
  return (
    <HStack gap="spacing2" justifyContent="flex-start" {...props}>
      <Text>Negotiation</Text>
      <Tooltip tooltip="Current status of negotiation. If empty, nothing has been requested by the client.">
        <Icon icon={IconName.QuestionMarkCircle} />
      </Tooltip>
    </HStack>
  );
};

export const NegotiationStatus = ({ entity, ...boxProps }: { entity: CustomerQuote } & BoxProps) => {
  return (
    <HStack gap="spacing2" justifyContent="flex-end" {...boxProps}>
      <Text color="colors.yellow.lighten">
        {entity.NegotiationStatus ? negotiationStatusMakerLabel(entity.NegotiationStatus) : '-'}
      </Text>
      {entity.NegotiationStatus && (
        <NegotiationStatusTooltip quote={entity}>
          <Icon
            icon={IconName.InformationCircle}
            color="colors.yellow.lighten"
            data-testid="negotiation-status-info-icon"
          />
        </NegotiationStatusTooltip>
      )}
    </HStack>
  );
};

function NegotiationStatusTooltip({ quote, children }: { quote: CustomerQuote; children: ReactNode }) {
  if (quote.unit !== Unit.Bps) {
    // We do not support anything other than Bps units for now.
    return children;
  }

  return (
    <Tooltip
      tooltip={
        <Flex flexDirection="column" gap="spacing4" data-testid="negotiation-status-tooltip-content">
          <Text>{negotiationStatusMakerDescription(quote.NegotiationStatus)}</Text>
          <Divider orientation="horizontal" />
          <HStack justifyContent="space-between">
            <Text color="colorTextImportant">Current quote</Text>
            <HStack justifyContent="space-between" gap="spacing4">
              <InlineFormattedNumber number={quote.bidPxBps} currency="BPS" increment="0" />
              /
              <InlineFormattedNumber number={quote.offerPxBps} currency="BPS" increment="0" />
            </HStack>
          </HStack>
        </Flex>
      }
    >
      {children}
    </Tooltip>
  );
}
